/**
 * 宣言したゴールの管理。
 *
 * 原典 GoalView の Enter（ゴール入力）／Space（選択）に相当する状態を持つ。
 *
 * - **一覧**は複数持てる。その日のタスクで狙うゴールを並べておく用途
 * - **選択は常に1つ**。会場によっては 370 点が地図に出るので、狙っている
 *   1点を赤い輪で囲んで目で見つけられるようにするのが選択の目的
 * - **設定高度**は PDG や FON のようにゴールに高度指定が要る課題で使う。
 *   高度を使わない課題では null のまま
 */

import {
  bandOf,
  fromEightDigit,
  fromGridCode,
  fromUtm,
  gridBandOf,
  toEightDigit,
  toGridCode,
  toUtm,
  utmDistance,
  type MarkPoint,
} from '@gv4d/core';

export interface DeclaredGoal {
  /**
   * 一覧内で一意。
   *
   * 会場の公式ゴール（番号入力の3桁・地図上のピン）は番号そのものを鍵にする
   * （`officialGoalKey`）ので、同じ番号を選び直しても増えずに選択だけ移る。
   *
   * それ以外（8デジットの座標入力・長押しで打った任意点）は**毎回新しい鍵**
   * を払い出す。8デジットは10m四方に丸められるため、これで重複判定すると
   * 至近距離の別の点（PZの矩形の角など）が同じマスに落ちて弾かれてしまう
   * ── これが実際に起きた不具合（本人確認、2026-08-10）。座標入力はそもそも
   * 同じ点を重ねて打つ用途もあるので、位置による重複排除自体を行わない。
   */
  key: string;
  /** 表示名。会場のゴール番号（"101" など）か、グリッド表記そのもの */
  name: string;
  lat: number;
  lon: number;
  /** グリッド表記（10m 単位。4桁/4桁が既定だが、表記設定次第で東距・北距が5桁になる） */
  eight: string;
  /**
   * `eight` のうち東距が占める桁数（4 or 5）。表示分割の根拠になる。
   * 無ければ`eastingDigitsOf()`が`eight`の長さから推測する（旧データ:
   * 8桁=4、9桁=5。旧版は東距だけが拡張対象だったのでこの推測で必ず正しい）
   */
  eastingDigits?: number;
  /** 設定高度 [ft]。指定しない課題では null */
  altFt: number | null;
  /**
   * 空中ゴールの判定容積（`altFt` があるときだけ意味を持つ）。
   *
   * ★ AXMER Chapter 15 の PDG（15.1）には高度・許容範囲の記述が無く、
   *   空中ゴールの寸法は各大会のタスクデータで決まる。したがってアプリ側で
   *   既定値をでっち上げず、入力されたときだけ容積として描く（未入力なら
   *   高度を持つ「点」として描くにとどめる）。
   */
  goalRadiusM?: number | null;
  /** 同、上下の許容差 [ft] */
  altToleranceFt?: number | null;
}

export interface GoalState {
  goals: DeclaredGoal[];
  /** 赤い輪で強調している 1 点。無選択なら null */
  selectedKey: string | null;
}

/**
 * 一覧内で一意な鍵を新しく払い出す（座標には基づかない）。
 * 位置で重複排除しない座標入力のゴールは、すべてここを通す。
 */
function freshGoalKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `g${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** 会場の公式ゴール（番号・ピン）の鍵。番号が同じなら常に同じ鍵になる */
export function officialGoalKey(name: string): string {
  return `mark:${name}`;
}

/**
 * 座標からゴールを1件作る。
 *
 * ★ `eight` を渡せるようにしてあるのは、**8デジットから復元した点で
 *   計算し直してはいけない**ため。復元した点は 10m マスのちょうど角に乗る
 *   （例: 東距 379940）。そこから緯度経度に直して UTM に戻すと丸め誤差で
 *   379939.999 になり、`toEightDigit` の floor が 1 マス下（7993）に落ちる。
 *   表示用の8デジットが呼ぶたびに変わってしまう。元の 8 デジットが
 *   分かっているときは、それをそのまま使うこと（鍵ではなく表示のための値）。
 */
export function makeGoal(
  name: string,
  lat: number,
  lon: number,
  eight?: string,
  eastingDigits?: number,
): DeclaredGoal {
  const code = eight ?? eightDigitOf(lat, lon);
  return { key: freshGoalKey(), name, lat, lon, eight: code, eastingDigits, altFt: null };
}

/**
 * 表示分割用の東距桁数を求める。保存値（`eastingDigits`）があればそれを、
 * 無ければ`eight`の長さから推測する（8桁=4、9桁=5。9桁は北距が5桁になる
 * 前の版では常に「東距5桁+北距4桁」だったので、この推測で必ず正しい）。
 */
export function eastingDigitsOf(g: { eight: string; eastingDigits?: number }): number {
  return g.eastingDigits ?? (g.eight.length === 8 ? 4 : 5);
}

/** ある座標の8デジット表記 */
export function eightDigitOf(lat: number, lon: number): string {
  const u = toUtm(lon, lat);
  return toEightDigit(u.easting, u.northing);
}

/** 会場のゴール地点（doc.kml 由来）から作る。番号が同じなら同じ鍵になる */
export function goalFromMark(m: MarkPoint): DeclaredGoal {
  return { ...makeGoal(m.name, m.lat, m.lon), key: officialGoalKey(m.name) };
}

/**
 * 会場のゴールから、8デジットが一致するものを探す。
 *
 * 8デジットで入力されたとき、それが会場のゴールと同じマスを指すなら、
 * 復元した「マスの角」ではなく**本物のゴール**を採る。正式な番号が付き、
 * 地図をタップして入れた場合と同じ 1 件に集約できる。
 */
export function findMarkByEight(marks: MarkPoint[], code: string): MarkPoint | null {
  return marks.find((m) => eightDigitOf(m.lat, m.lon) === code) ?? null;
}

/**
 * 8デジットを座標に戻す。
 *
 * 8デジットは 100km 区画の情報を持たないため、基準が要る。原典は地図外に
 * 出た場合に隣接 8 方向の区画を探索して収めている（`GoalView.cpp:1655-1718`）
 * ので、こちらも 3×3 の区画を試して**基準地点に最も近い解**を採る。
 * 会場が区画の境目にあっても正しい点が選ばれる。
 */
export function resolveEightDigit(
  code: string,
  ref: { lat: number; lon: number },
): { lat: number; lon: number } | null {
  if (!/^\d{8}$/.test(code)) return null;
  const refUtm = toUtm(ref.lon, ref.lat);
  const base = bandOf(refUtm);

  let best: { d: number; lat: number; lon: number } | null = null;
  for (let de = -1; de <= 1; de++) {
    for (let dn = -1; dn <= 1; dn++) {
      const p = fromEightDigit(code, {
        eastingBase: base.eastingBase + de * 100000,
        northingBase: base.northingBase + dn * 100000,
        zone: base.zone,
      });
      if (!p) continue;
      const d = utmDistance(p, refUtm);
      if (!best || d < best.d) best = { d, ...fromUtm(p) };
    }
  }
  return best ? { lat: best.lat, lon: best.lon } : null;
}

// --- 桁数表記の切り替え（4/4・5/4・4/5・5/5） --------------------------------
//
// 大会によっては東距・北距をそれぞれ独立に5桁（1000km区画基準）で表す
// 運用がある（UTMゾーンの境目付近の会場向け、GV4D独自拡張）。上の
// `eightDigitOf`/`resolveEightDigit`/`findMarkByEight` は8桁固定のまま残し、
// こちらは表記を呼び分けるラッパー。

/** 東距の桁数（4 or 5） */
export function gridEastingDigits(wideEasting: boolean): number {
  return wideEasting ? 5 : 4;
}

/** 北距の桁数（4 or 5） */
export function gridNorthingDigits(wideNorthing: boolean): number {
  return wideNorthing ? 5 : 4;
}

/** 現在の表記設定での総桁数（8〜10） */
export function gridCodeDigitCount(wideEasting: boolean, wideNorthing: boolean): number {
  return gridEastingDigits(wideEasting) + gridNorthingDigits(wideNorthing);
}

/** ある座標のグリッド表記（表記設定に応じて東距・北距それぞれ4桁 or 5桁） */
export function gridCodeOf(lat: number, lon: number, wideEasting: boolean, wideNorthing: boolean): string {
  const u = toUtm(lon, lat);
  return toGridCode(u.easting, u.northing, wideEasting, wideNorthing);
}

/**
 * 一覧に出す用のグリッド表記。`gridCodeOf`と違い、**10mマスの角にちょうど乗っている点**でもずれない。
 *
 * ★ グリッド表記から復元した点（TaskBoard の座標・MDT の基準点など）は10mマスの角に乗る。
 *   緯度経度へ直して戻すと丸め誤差で 379939.999 のようになり、切り捨てで1マス下に落ちる
 *   （"1600/8000" が "1600/7999" と出る。実際に起きた）。位置が合っているか確かめるために出す
 *   表記がずれては意味が無いので、1cmだけ内側に寄せてから切り捨てる（マスは10mなので他の点には影響しない）。
 *   `makeGoal`のコメントにある「復元した点で計算し直してはいけない」と同じ理由。
 */
export function gridCodeForDisplay(lat: number, lon: number, wideEasting: boolean, wideNorthing: boolean): string {
  const u = toUtm(lon, lat);
  return toGridCode(u.easting + 0.01, u.northing + 0.01, wideEasting, wideNorthing);
}

/**
 * グリッド表記（"1523 0769"等）を座標に戻すときの基準点にする、会場の代表地点。
 *
 * グリッド表記は100km区画を持たないため、基準点から最も近い区画に決め打ち
 * される。手元のGPS位置を基準にすると、会場から離れた場所（自宅など）で
 * 入力・取込みしたときに別の区画へずれる（一関のゴールが、東京近郊で
 * 取り込むと千葉県に出た）。入力されるのは会場の競技範囲内の座標なので、
 * 選択中の会場（の競技地図）を基準にする。
 *
 * 校正点（競技地図の四隅等）の平均を優先し、無ければ会場のゴールの平均。
 * どちらも無ければnull（呼び出し側で手元の位置・地図の中心へ退避する）。
 */
export function venueReferencePoint(
  venue: {
    calibration: { points: ReadonlyArray<{ lat: number; lon: number }> } | null;
    goals: ReadonlyArray<{ lat: number; lon: number }>;
  } | null,
): { lat: number; lon: number } | null {
  if (!venue) return null;
  const calPoints = venue.calibration?.points ?? [];
  const pts = calPoints.length > 0 ? calPoints : venue.goals;
  if (pts.length === 0) return null;
  let lat = 0;
  let lon = 0;
  for (const p of pts) {
    lat += p.lat;
    lon += p.lon;
  }
  return { lat: lat / pts.length, lon: lon / pts.length };
}

/**
 * グリッド表記を座標に戻す（表記設定に応じて東距・北距それぞれ4桁 or 5桁）。
 * `resolveEightDigit` と同じ3×3区画探索を、表記に応じた区画サイズで行う。
 * 両方とも4桁（既定）なら、検証済みの`resolveEightDigit`をそのまま使う。
 */
export function resolveGridCode(
  code: string,
  ref: { lat: number; lon: number },
  wideEasting: boolean,
  wideNorthing: boolean,
): { lat: number; lon: number } | null {
  if (!wideEasting && !wideNorthing) return resolveEightDigit(code, ref);

  const refUtm = toUtm(ref.lon, ref.lat);
  const base = gridBandOf(refUtm, wideEasting, wideNorthing);
  const eStep = wideEasting ? 1_000_000 : 100000;
  const nStep = wideNorthing ? 1_000_000 : 100000;
  let best: { d: number; lat: number; lon: number } | null = null;
  for (let de = -1; de <= 1; de++) {
    for (let dn = -1; dn <= 1; dn++) {
      const p = fromGridCode(code, wideEasting, wideNorthing, {
        eastingBase: base.eastingBase + de * eStep,
        northingBase: base.northingBase + dn * nStep,
        zone: base.zone,
      });
      if (!p) continue;
      const d = utmDistance(p, refUtm);
      if (!best || d < best.d) best = { d, ...fromUtm(p) };
    }
  }
  return best ? { lat: best.lat, lon: best.lon } : null;
}

/** 会場のゴールから、グリッド表記（表記設定に応じて4桁 or 5桁）が一致するものを探す */
export function findMarkByGridCode(
  marks: MarkPoint[],
  code: string,
  wideEasting: boolean,
  wideNorthing: boolean,
): MarkPoint | null {
  return marks.find((m) => gridCodeOf(m.lat, m.lon, wideEasting, wideNorthing) === code) ?? null;
}

/** 2地点間の距離 [m]。競技と同じく UTM 平面で測る */
export function distanceMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  return utmDistance(toUtm(a.lon, a.lat), toUtm(b.lon, b.lat));
}

/**
 * a から b への方位 [度]。
 * 競技では方眼（UTM グリッド）北を基準に読むので、真北ではなくこちらで出す。
 */
export function bearingDeg(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const ua = toUtm(a.lon, a.lat);
  const ub = toUtm(b.lon, b.lat);
  const deg = (Math.atan2(ub.easting - ua.easting, ub.northing - ua.northing) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/**
 * 現在地から選択中ゴールへの点線を、距離・方位のラベル付きで地図に描くための
 * GeoJSON。`distanceMeters`/`bearingDeg`と同じくグリッド北基準・UTM平面で
 * 測る（`goalMetricsHtml`の一覧表示と同じ数字になる）。
 */
export function goalBearingLineToGeoJson(
  here: { lat: number; lon: number },
  goal: { lat: number; lon: number },
): GeoJSON.FeatureCollection {
  const distanceKm = distanceMeters(here, goal) / 1000;
  const bearing = Math.round(bearingDeg(here, goal));
  const label = `${bearing}° / ${distanceKm.toFixed(2)} km`;
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { label },
        geometry: {
          type: 'LineString',
          coordinates: [
            [here.lon, here.lat],
            [goal.lon, goal.lat],
          ],
        },
      },
    ],
  };
}

// --- 保存 -------------------------------------------------------------------

const KEY_PREFIX = 'gv4d-goals:';

/**
 * ゴールは会場ごとに保存する。会場を切り替えたときに前の会場のゴールが
 * 残っていると、地図の外に赤い輪だけが漂うことになる。
 */
export function loadGoals(venueId: string): GoalState {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + venueId);
    if (!raw) return { goals: [], selectedKey: null };
    const parsed = JSON.parse(raw) as GoalState;
    if (!Array.isArray(parsed.goals)) return { goals: [], selectedKey: null };
    // 選択が一覧から消えている状態を残さない
    const selectedKey = parsed.goals.some((g) => g.key === parsed.selectedKey)
      ? parsed.selectedKey
      : null;
    return { goals: parsed.goals, selectedKey };
  } catch {
    return { goals: [], selectedKey: null };
  }
}

export function saveGoals(venueId: string, s: GoalState): void {
  try {
    localStorage.setItem(KEY_PREFIX + venueId, JSON.stringify(s));
  } catch {
    // 保存できなくても、その場の操作は続けられる
  }
}
